'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { NewsImageMarquee } from '@/components/layout/news-image-marquee'
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  LivingThermal,
  GliderBackdrop, GliderOrbit,
} from '@/components/award'

interface NewsTranslationSlot {
  title?: string
  excerpt?: string
  image_alt?: string
}

interface NewsListEntry {
  id: string
  title: string
  slug: string
  category: 'PLATFORM' | 'TECH' | 'COMMUNITY' | 'WEATHER'
  excerpt: string | null
  image_url: string | null
  image_alt: string | null
  youtube_video_id: string | null
  published_at: string | null
  view_count: number
  translations_json: Record<string, NewsTranslationSlot> | null
  author: { name: string | null }
}

/**
 * Waehle den Uebersetzungs-Slot fuer die aktuelle UI-Sprache aus.
 * DE = Quelle, fuer alle anderen Locales versuchen wir translations_json[locale].
 * Fallback: deutsches Original (title / excerpt).
 */
function pickNewsSlot(entry: NewsListEntry, locale: string): { title: string; excerpt: string | null; imageAlt: string | null } {
  if (locale === 'de' || !entry.translations_json) {
    return { title: entry.title, excerpt: entry.excerpt, imageAlt: entry.image_alt }
  }
  const slot = entry.translations_json[locale]
  return {
    title: (slot?.title && slot.title.trim()) || entry.title,
    excerpt: (slot?.excerpt && slot.excerpt.trim()) || entry.excerpt,
    imageAlt: (slot?.image_alt && slot.image_alt.trim()) || entry.image_alt,
  }
}

const CATEGORY_ACCENTS: Record<string, string> = {
  PLATFORM: '#2a6e82',
  TECH: '#7c3aed',
  COMMUNITY: '#16a34a',
  WEATHER: '#d97706',
}

export default function NewsListPage() {
  const { t, locale } = useTranslation()
  const CATEGORY_LABELS: Record<string, string> = {
    PLATFORM: t('news.categoryPlatform'),
    TECH: t('news.categoryTech'),
    COMMUNITY: t('news.categoryCommunity'),
    WEATHER: t('news.categoryWeather'),
  }
  const [entries, setEntries] = useState<NewsListEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    const url = activeCategory ? `/api/news?category=${activeCategory}&limit=50` : '/api/news?limit=50'
    fetch(url)
      .then((r) => r.json())
      .then((data) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [activeCategory])

  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen" style={{ background: 'var(--award-ink)' }}>
        <FglHeader />
        {/* r19.55.45 (Stephan-Direktive 2026-07-13): NewsImageMarquee wurde
            von der Landing-Page hierher auf /news verschoben - hier gehoert
            sie als Uebersicht der letzten News-Highlights hin. */}
        <NewsImageMarquee />
      <GliderBackdrop />

        {/* HERO */}
        <section className="relative pt-[130px] pb-[80px] md:pt-[180px] md:pb-[110px] overflow-hidden">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="award-eyebrow">{t('news.pageEyebrow', { count: entries.length })}</span>
                <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                <span className="award-label">{t('news.categoriesLabel')}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <h1 className="award-h-mega">
                    {t('news.heroTitle1')} <span className="award-grad-text">{t('news.heroTitleAccent')}</span>
                  </h1>
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  <p className="award-body-lead">
                    {t('news.heroLead')}
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* FILTER BAR */}
        <div
          className="sticky top-[64px] z-30 border-b backdrop-blur-xl"
          style={{ background: 'color-mix(in oklch, var(--award-ink-2) 85%, transparent)', borderColor: 'var(--award-line)' }}
        >
          {/* 2026-06-19 Pillen-Polish: Filter-Pills auf wertung-tab-pill mit
              data-state. Active-State liefert Gold-Glas + Glow analog /wertung-
              Tabs. Old .award-cat-pill bleibt im <style jsx> bestehen, falls
              andere News-Subkomponenten sie noch nutzen. */}
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-3">
            <span className="award-label">{t('news.filterLabel')}</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                data-state={activeCategory === null ? 'active' : 'inactive'}
                className="wertung-tab-pill px-4 py-1.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-[0.08em]"
              >
                {t('news.filterAll')}
              </button>
              {Object.entries(CATEGORY_LABELS).map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setActiveCategory(code)}
                  data-state={activeCategory === code ? 'active' : 'inactive'}
                  className="wertung-tab-pill px-4 py-1.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-[0.08em]"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* LIST */}
        <section className="py-16 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            {loading ? (
              <div className="py-20 flex justify-center">
                <div
                  className="w-8 h-8 rounded-full border-2 border-transparent animate-spin"
                  style={{ borderTopColor: 'var(--award-gold)', borderRightColor: 'var(--award-gold)' }}
                />
              </div>
            ) : entries.length === 0 ? (
              <div className="py-20 text-center" style={{ color: 'var(--award-cirrus-3)' }}>
                <div className="award-eyebrow mb-3">{t('news.emptyEyebrow')}</div>
                <h2 className="award-h-md mb-2">{t('news.emptyTitle')}</h2>
                <p className="text-[14px]">
                  {activeCategory
                    ? t('news.emptyHintCategory', { category: CATEGORY_LABELS[activeCategory] })
                    : t('news.emptyHintGeneric')}
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {entries.map((entry, i) => {
                  const accent = CATEGORY_ACCENTS[entry.category] || 'var(--award-gold)'
                  const slot = pickNewsSlot(entry, locale)
                  const localeDate = ({ 'zh-CN':'zh-CN','ja':'ja','ko':'ko','th':'th','he':'he' } as Record<string,string>)[locale] || locale
                  return (
                    <Reveal key={entry.id} delay={(i % 5) as 0 | 1 | 2 | 3 | 4}>
                      <Link
                        href={`/news/${entry.slug}`}
                        className="award-news-card group"
                      >
                        <div className="flex flex-col sm:flex-row gap-5 sm:gap-7 items-start">
                          {(entry.image_url || entry.youtube_video_id) && (
                            <div className="flex-shrink-0 w-full sm:w-[220px] aspect-video rounded-[14px] overflow-hidden relative">
                              {entry.youtube_video_id ? (
                                <>
                                  <img
                                    src={`https://i.ytimg.com/vi/${entry.youtube_video_id}/hqdefault.jpg`}
                                    alt={slot.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                    <div
                                      className="w-12 h-12 rounded-full flex items-center justify-center"
                                      style={{ background: 'rgba(0,0,0,0.6)' }}
                                    >
                                      <span className="text-white text-lg ms-1 rtl:scale-x-[-1]">▶</span>
                                    </div>
                                  </div>
                                </>
                              ) : entry.image_url ? (
                                <img
                                  src={entry.image_url}
                                  alt={slot.imageAlt || slot.title}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : null}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            {/* 2026-06-19 Pillen-Polish: Kategorie-Tag innerhalb
                                der News-Karte auf wertung-flight-pill (Glas +
                                Bevel + per-Element-Farbe). */}
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <span
                                className="wertung-flight-pill px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-[0.16em]"
                                style={{ ['--pill-color' as any]: accent }}
                              >
                                {CATEGORY_LABELS[entry.category]}
                              </span>
                              {entry.published_at && (
                                <span className="award-label">
                                  {new Date(entry.published_at).toLocaleDateString(localeDate, {
                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                  })}
                                </span>
                              )}
                            </div>
                            <h2
                              className="text-[20px] sm:text-[26px] leading-tight mb-3 transition-colors"
                              style={{
                                fontFamily: 'var(--font-display)',
                                fontWeight: 600,
                                letterSpacing: '-0.015em',
                                color: 'var(--award-cirrus)',
                              }}
                            >
                              {slot.title}
                              <span className="inline-block ms-2 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 rtl:scale-x-[-1]" style={{ color: 'var(--award-gold)' }}>→</span>
                            </h2>
                            {slot.excerpt && (
                              <p className="text-[14px] leading-relaxed line-clamp-2" style={{ color: 'var(--award-cirrus-2)' }}>
                                {slot.excerpt}
                              </p>
                            )}
                            <div className="mt-4 flex items-center gap-3 text-[12px]" style={{ color: 'var(--award-cirrus-3)' }}>
                              {entry.author?.name && <span>{t('news.by')} {entry.author.name}</span>}
                              <span>·</span>
                              <span>{t('news.viewsCount', { count: entry.view_count })}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </Reveal>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* CLOSING */}
        <section
          className="py-20 md:py-28 border-t relative overflow-hidden"
          style={{ borderColor: 'var(--award-line)', background: 'var(--award-ink-2)' }}
        >
          <div className="award-thermal-dot" style={{ left: '15%', top: '30%' }} />
          <div className="award-thermal-dot" style={{ left: '85%', top: '60%', animationDelay: '1.5s' }} />
          <div className="max-w-[1100px] mx-auto px-6 text-center relative z-10">
            <Reveal>
              <h3 className="award-h-lg mb-8">
                {t('news.closingTitle1')} <span className="award-grad-text">{t('news.closingTitleAccent')}</span>
              </h3>
            </Reveal>
            <Reveal delay={1}>
              <div className="flex gap-4 justify-center flex-wrap">
                <Magnetic>
                  <Link href="/register" className="award-btn award-btn-primary">
                    {t('news.closingCtaRegister')} <span aria-hidden>→</span>
                  </Link>
                </Magnetic>
                <Magnetic>
                  <Link href="/" className="award-btn award-btn-ghost">
                    {t('news.closingCtaHome')}
                  </Link>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </section>

        <style jsx global>{`
          .award-cat-pill {
            display: inline-flex;
            align-items: center;
            padding: 7px 14px;
            border-radius: 999px;
            font-family: var(--font-mono);
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--award-cirrus-3);
            background: transparent;
            border: 1px solid var(--award-line);
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .award-cat-pill:hover {
            color: var(--award-cirrus);
            border-color: var(--award-line-hi);
          }
          .award-cat-pill.is-active {
            background: var(--award-cirrus);
            color: var(--award-ink-2);
            border-color: var(--award-cirrus);
          }
          .award-cat-tag {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 999px;
            font-family: var(--font-mono);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: var(--cat-accent, var(--award-gold));
            background: color-mix(in oklch, var(--cat-accent, var(--award-gold)) 12%, transparent);
            border: 1px solid color-mix(in oklch, var(--cat-accent, var(--award-gold)) 30%, transparent);
          }
          .award-news-card {
            display: block;
            padding: 22px 24px;
            background: var(--award-ink-2);
            border: 1px solid var(--award-line);
            border-radius: 20px;
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .award-news-card:hover {
            border-color: var(--award-gold);
            background: var(--award-ink-3);
            transform: translateY(-2px);
            box-shadow: 0 14px 38px -22px var(--award-gold-glow);
          }
        `}</style>
      </div>
    </LenisProvider>
  )
}
